import { Box } from "@mui/material";
import { Form, Formik, FormikProps } from "formik";
import { RefObject } from "react";
import * as yup from "yup";

import { updateEditWBSEntry } from "@/features/projectScope/helpers/projectScopeHelpers";
import editDeliverable from "@/shared/api/editDeliverable";
import { RTextFormInput } from "@/shared/components/forms";
import {
  FIELD_LENGTH,
  FIELD_REQUIRED,
  LONG_DESCRIPTION,
  LONG_NAMES,
} from "@/shared/constants";
import { WBSEntryViewModel } from "@/shared/context/projectWBS/projectWBSModel";
import useProjectWBSContext from "@/shared/context/projectWBS/useProjectWBSContext";
import { validateFormFields } from "@/shared/utils/formValidation";

import { EditWBSEntry } from "../../types/projectScopeModels";

export type EditProjectScopeFormProps = {
  WBSEntry: WBSEntryViewModel;
  innerRef: RefObject<FormikProps<EditWBSEntry>>;
  setIsFormValid?: (isValid: boolean) => void;
  handleClose: () => void;
};

const validationSchema = yup.object({
  name: yup.string().required(FIELD_REQUIRED).max(LONG_NAMES, FIELD_LENGTH),
  description: yup.string().max(LONG_DESCRIPTION, FIELD_LENGTH).notRequired(),
});

export default function EditWBSEntryForm({
  WBSEntry,
  innerRef,
  setIsFormValid,
  handleClose,
}: EditProjectScopeFormProps) {
  const { refetchProjectWBS } = useProjectWBSContext();

  return (
    <Formik
      initialValues={{
        name: WBSEntry.name,
        description: WBSEntry.description,
      }}
      validationSchema={validationSchema}
      innerRef={innerRef}
      validateOnMount={true}
      onSubmit={async (newValues) => {
        try {
          const editedWBSEntry = updateEditWBSEntry(WBSEntry, newValues);
          await editDeliverable(editedWBSEntry);
          handleClose();
          refetchProjectWBS();
        } catch (error) {
          // Handle error
        }
      }}>
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        isValid,
        validateField,
        setFieldTouched,
      }) => {
        // Set form validity
        validateFormFields<EditWBSEntry>(
          isValid,
          touched,
          ["name"],
          setIsFormValid,
          true
        );

        return (
          <Box sx={{ "& .MuiTextField-root": { mb: 2 }, paddingTop: "1rem" }}>
            <Form>
              <RTextFormInput
                required
                name="name"
                label="Deliverable Name"
                value={values.name}
                errors={errors}
                touched={touched}
                handleChange={handleChange}
                handleBlur={handleBlur}
                setFieldTouched={setFieldTouched}
                validateField={validateField}
                maxLength={LONG_NAMES}
              />
              <RTextFormInput
                label="Statement"
                name="description"
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
                errors={errors}
                touched={touched}
                handleChange={handleChange}
                handleBlur={handleBlur}
                setFieldTouched={setFieldTouched}
                validateField={validateField}
                maxLength={LONG_DESCRIPTION}
                multiline
                rows={5}
              />
            </Form>
          </Box>
        );
      }}
    </Formik>
  );
}
